import React, { useState, useEffect } from 'react';
import useTranslation from './useTranslation';
import { passwordService } from '../services/passwordService';
import config from '../config';
import LanguageSwitcher from './LanguageSwitcher';
import '../css/passwordgate.css';

function PasswordGate({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('is_authenticated') === 'true';
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  const logo = config.assets.logo;

  // Verifica lo stato della password
  const checkPasswordValidity = async () => {
    if (!config.components.password.enabled) {
      setIsLoading(false);
      return true;
    }

    try {
      const isValid = await passwordService.verifyPassword();
      if (!isValid) {
        localStorage.removeItem('is_authenticated');
        setIsAuthenticated(false);
      }
      return isValid;
    } catch (error) {
      console.error('Errore durante la verifica della password:', error);
      
      // Rimosso is_authenticated solo se l'errore è specifico e non un problema di rete
      const isSpecificError = error.message === 'Password disattivata' || 
                             error.message === 'Password scaduta' ||
                             error.message.includes('Troppi tentativi');
      
      if (isSpecificError) {
        localStorage.removeItem('is_authenticated');
        setIsAuthenticated(false);
      }
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Verifica iniziale della password
  useEffect(() => {
    const initialCheck = async () => {
      if (!config.components.password.enabled) {
        setIsLoading(false);
        return;
      }

      // Se non c'è una password salvata, non serve verificare
      if (!localStorage.getItem('is_authenticated')) {
        setIsLoading(false);
        return;
      }

      await checkPasswordValidity();
    };

    initialCheck();
  }, []);

  // Verifica la password prima di ogni cambio pagina
  useEffect(() => {
    if (!isAuthenticated || !config.components.password.enabled) return;

    const handleNavigation = () => {
      checkPasswordValidity();
    };

    // Eventi di navigazione
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('pushState', handleNavigation);
    window.addEventListener('replaceState', handleNavigation);

    // Rimossa verifica periodica ogni minuto
    
    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('pushState', handleNavigation);
      window.removeEventListener('replaceState', handleNavigation);
    };
  }, [isAuthenticated]);

  // Modifica il corpo impostando lo scroll a nascosto per la pagina di login
  /*
  useEffect(() => {
    if (!isAuthenticated) {
      document.body.style.padding = '0';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.padding = '60px 0 80px 0';
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.padding = '60px 0 80px 0';
      document.body.style.overflow = '';
    };
  }, [isAuthenticated]);
  */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const isValid = await passwordService.verifyPassword(password);
      if (isValid) {
        setIsAuthenticated(true);
        localStorage.setItem('is_authenticated', 'true');
      } else {
        throw new Error(t('passwordNonValida'));
      }
    } catch (error) {
      setError(error.message || 'Password non valida. Riprova.');
      setPassword('');
      localStorage.removeItem('is_authenticated');
    } finally {
      setIsLoading(false);
    }
  };

  // Se il componente password non è abilitato, mostra direttamente i children
  if (!config.components.password.enabled) {
    return (
      <>
        {children}
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <div className="loading-container">{t('caricamento')}</div>
      </>
    );
  }

  // Se è autenticato, mostra direttamente il contenuto
  if (isAuthenticated) {
    return (
      <>
        {children}
      </>
    );
  }

  // Altrimenti mostra il form di login
  return (
    <>
      <div className="password-container">
        {/* Language Switcher separato */}
        <div className="language-switcher-container">
          <LanguageSwitcher />
        </div>
        
        {/* Logo */}
        <div className="logo-container">
          <img 
            src={logo} 
            alt="Walky Logo" 
            className="password-logo"
          />
        </div>
        
        <div className="password-content">
          <h3 className="password-title font-titillium titillium-web-bold">{t('benvenutoWalky')}</h3>
          <p className="password-description font-titillium titillium-web-regular">{t('inserisciPassword')}</p>
          
          <form onSubmit={handleSubmit} className="password-form">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('passwordPlaceholder')}
              required
              className="password-input"
            />
            <button 
              type="submit" 
              className="password-button btn"
            >
              {t('accedi')}
            </button>
            
            {error && (
              <div className="password-error">
                {passwordService.translateErrorMessage(error)}
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
}

export default PasswordGate; 